import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '@/lib/api';
import { normalizeOperatorDashboard } from '@/lib/normalize';
import { collectionLabel, formatDelta, formatHours, formatPercent, orDash } from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { StatTile } from '@/ui/stat-tile';
import { Segmented } from '@/ui/segmented';
import { FilterChip } from '@/ui/filter-chip';
import { MicroLabel } from '@/ui/micro-label';
import { Input } from '@/ui/input';
import { Tooltip } from '@/ui/tooltip';
import { ErrorState } from '@/ui/error-state';
import { DataTable, type Column } from '@/ui/data-table';
import { ModalityChips } from '@/domain/modality-chips';
import { TierBadge } from '@/domain/tier-badge';
import {
  HoursByModality,
  CollectionDonut,
  UploadSparkline,
  toModalitySeries,
  toCollectionSeries
} from './dashboard-charts';
import type { VendorRow } from '@/lib/contract';

const RANGES = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: 'all', label: 'All' }
];

const ALL = [{ value: 'all', label: 'All' }];

/** The five header filters, each a real query parameter the API applies in SQL. */
const FILTERS = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'modality', label: 'Modality' },
  { key: 'collection', label: 'Collection' },
  { key: 'format', label: 'Format' },
  { key: 'tier', label: 'Tier' }
] as const;
type FilterKey = (typeof FILTERS)[number]['key'];

const matrixColumns: Column<VendorRow>[] = [
  { key: 'vendor', header: 'Vendor', render: r => r.name },
  {
    key: 'hours',
    header: 'Hours',
    className: 'w-[80px]',
    render: r => <span className="font-mono text-[13px]">{formatHours(r.hours)}</span>
  },
  {
    key: 'modalities',
    header: 'Modalities',
    className: 'w-[190px]',
    render: r => <ModalityChips modalities={r.modalities} />
  },
  {
    key: 'collection',
    header: 'Collection',
    className: 'w-[150px] whitespace-nowrap',
    render: r => (
      <span className="text-[13px] text-muted">
        {r.collections.length ? r.collections.map(collectionLabel).join(', ') : '—'}
      </span>
    )
  },
  {
    key: 'formats',
    header: 'Formats',
    className: 'w-[120px]',
    render: r => <span className="text-[13px] text-muted">{r.formats.length ? r.formats.join(', ') : '—'}</span>
  },
  {
    key: 'qa',
    header: 'QA pass',
    className: 'w-[80px]',
    render: r => <span className="font-mono text-[13px] text-pass">{formatPercent(r.qa_pass_rate)}</span>
  },
  { key: 'tier', header: 'Tier', className: 'w-[60px]', render: r => <TierBadge tier={r.tier} /> }
];

// TARGET fields that have no column on VendorRow yet; folded in here rather than widening
// the contract for a single view.

export function InternalDashboard() {
  const [range, setRange] = useState('30d');
  const [selected, setSelected] = useState<Partial<Record<FilterKey, string>>>({});

  // The range was in the query key but never in the request, so the control looked live and changed
  // nothing. Both it and the filters are query parameters the API applies in SQL.
  const params = new URLSearchParams({ range });
  for (const [key, value] of Object.entries(selected)) if (value && value !== 'all') params.set(key, value);

  const { data, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'dashboard', range, selected],
    queryFn: async () => normalizeOperatorDashboard(await api(`/dashboard/stats?${params}`)),
    // Keeps the previous figures on screen while a range or filter change is in flight, instead of
    // blanking every tile to an em dash on each keystroke.
    placeholderData: previous => previous,
    // Server-computed totals over every tenant, with no channel that covers them. A console left
    // open otherwise shows the figures from whenever it was opened.
    refetchInterval: 60000
  });

  const hours = data?.total_hours;
  const vendors = data?.active_vendors;
  const pass = data?.quality_pass_rate;
  const flags = data?.open_flags;
  const activity = data?.upload_activity ?? [];

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Dashboard"
        subtitle={
          data
            ? `Vendor + dataset activity · ${data.datasets} datasets · ${data.tenants} vendors`
            : 'Vendor + dataset activity'
        }
        actions={<Segmented segments={RANGES} value={range} onChange={setRange} />}
      />

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(({ key, label }) => (
          <FilterChip
            key={key}
            label={label}
            value={selected[key] ?? 'all'}
            options={[...ALL, ...(data?.filter_options?.[key] ?? [])]}
            onChange={value => setSelected(prev => ({ ...prev, [key]: value }))}
          />
        ))}
        {Object.values(selected).some(v => v && v !== 'all') ? (
          <button
            type="button"
            onClick={() => setSelected({})}
            className="cursor-pointer text-[12px] text-primary hover:underline"
          >
            Reset
          </button>
        ) : null}
        <Tooltip label="Search is not wired up in this build">
          <div className="relative ml-auto w-[260px] max-md:w-full">
            <Search className="absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-dim" />
            <Input
              placeholder="search vendors, datasets…"
              disabled
              aria-disabled
              className="cursor-not-allowed pl-8 text-[13px] opacity-60"
            />
          </div>
        </Tooltip>
      </div>

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load the dashboard" />
      ) : null}

      <div className="grid grid-cols-5 gap-4 max-lg:grid-cols-3 max-sm:grid-cols-1">
        <StatTile
          label="Hours of data"
          value={hours ? formatHours(hours.value) : '—'}
          tone="primary"
          foot={hours ? formatDelta(hours.delta, 'h', 'vs prior 30d') : undefined}
          footTone="pass"
        />
        <StatTile
          label="Datasets"
          value={orDash(data?.datasets)}
          foot={data?.new_datasets_in_range ? `+${data.new_datasets_in_range} new in range` : undefined}
          footTone="pass"
        />
        <StatTile
          label="Active vendors"
          value={orDash(vendors?.count)}
          foot={vendors ? `${vendors.submitted_in_range} submitted in range` : undefined}
        />
        <StatTile
          label="Quality pass rate"
          value={pass ? formatPercent(pass.value) : '—'}
          foot={pass ? formatDelta(pass.delta, 'pts', 'vs prior 30d') : undefined}
          footTone="danger"
        />
        <StatTile
          label="Open flags"
          value={orDash(flags?.count)}
          foot={flags ? `${flags.blocking} blocking release` : undefined}
          footTone="danger"
        />
      </div>

      <div className="grid grid-cols-[1fr_320px_320px] gap-4 max-xl:grid-cols-[1fr_300px] max-lg:grid-cols-1">
        <Panel title="Hours by modality" subtitle="Collected in range">
          <HoursByModality data={toModalitySeries(data?.hours_by_modality ?? [])} />
        </Panel>
        <Panel title="Collection types" subtitle="Share of hours">
          <CollectionDonut data={toCollectionSeries(data?.collection_share ?? [])} />
        </Panel>
        <Panel title="Formats & containers" subtitle={`Across ${data?.datasets ?? 0} datasets`}>
          <ul className="grid gap-2.5">
            {(data?.formats ?? []).map(format => (
              <li key={format.label} className="flex items-center justify-between gap-4">
                <span className="text-[13px] text-muted">{format.label}</span>
                <span className="rounded-[3px] bg-raised px-2 py-0.5 font-mono text-[11px] text-foreground">
                  {format.count}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-4 max-lg:grid-cols-1">
        <Panel title="Vendor capability matrix" subtitle="Hours · modalities · QA · tier" bodyClassName="px-0 pb-0">
          <DataTable
            columns={matrixColumns}
            rows={data?.capability_matrix ?? []}
            rowKey={row => row.id}
            className="rounded-none border-0 bg-transparent"
          />
        </Panel>
        <Panel title="Upload activity" subtitle="Hours received / week">
          <UploadSparkline data={activity} />
          <div className="mt-3 flex justify-between">
            <MicroLabel size="xs">{activity[0]?.week}</MicroLabel>
            <MicroLabel size="xs">{activity[activity.length - 1]?.week}</MicroLabel>
          </div>
        </Panel>
      </div>
    </div>
  );
}
